import { academyVersionRequest, createVersionedCommandSender } from "../../../shared/academyApi";

export type ProctorReadinessKind = "identity" | "device" | "environment";

export type ProctorSession = {
  exam_session_id: string;
  assessment_title: string;
  cohort_name: string;
  starts_at: string;
  ends_at: string;
  state: string;
};

export type ProctorCandidate = {
  exam_session_id: string;
  candidate_user_id: string;
  candidate_number: string;
  full_name: string;
  cohort_name: string;
  assessment_title: string;
  state: string;
  presence_state: string;
  time_remaining_seconds: number | null;
  identity_status: string;
  device_status: string;
  environment_status: string;
  admission_version: number;
  admission_decided_at: string | null;
};

export type ProctorReadinessRecord = {
  id: string;
  exam_session_id: string;
  candidate_user_id: string;
  check_type: ProctorReadinessKind;
  status: string;
  method: string;
  observations: string;
  recorded_by: string;
  recorded_at: string;
  version: number;
  schema_version: "d12-candidate-readiness.v1";
  candidate_registration_id: string;
  proctor_assignment_id: string;
  proctor_assignment_version: number;
  authority_snapshot_hash: string;
};

export type ProctorReentryCandidate = {
  candidate_registration_id: string;
  candidate_user_id: string;
  candidate_number: string;
  full_name: string;
  current_entry_sequence: number;
  max_reentries: number;
  pending_approval_id: string | null;
  pending_approval_sequence: number | null;
  can_approve: boolean;
};

export type ProctorRuntimeClock = {
  exam_session_id: string;
  assessment_id: string;
  assessment_title: string;
  starts_at: string;
  ends_at: string;
  effective_ends_at: string;
  server_now: string;
  clock_state: "scheduled" | "running" | "paused" | "ended";
  duration_seconds: number;
  elapsed_seconds: number;
  remaining_seconds: number;
  paused_seconds: number;
  pause_count: number;
  open_pause_id: string | null;
  entered_candidate_count: number;
  admissions_locked: boolean;
  clock_schema: "d12-exam-runtime-clock.v3";
  clock_version: 3;
};

export type SessionAdmissionLockReceipt = {
  id: string;
  exam_session_id: string;
  locked_at: string;
  schema_version: "d12-session-admission-lock.v1";
  control: "lock_new_admissions";
  existingAdmissionsChanged: false;
  attemptChanged: false;
  academicOutcome: false;
};

export type SessionTimerEventReceipt = {
  id: string;
  exam_session_id: string;
  sequence: number;
  event_type: "paused" | "resumed";
  pause_id: string;
  occurred_at: string;
  schema_version: "d12-session-timer-event.v1";
  control: "pause_timer" | "resume_timer";
  extensionApplied: false;
  attemptChanged: false;
  academicOutcome: false;
};

export type RecordReadinessInput = {
  candidateUserId: string;
  status: string;
  method: string;
  observations: string;
};

const object = (value: unknown, label: string): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${label} is invalid`);
  return value as Record<string, unknown>;
};
const text = (row: Record<string, unknown>, key: string) => {
  if (typeof row[key] !== "string" || !row[key]) throw new Error(`Readiness response is missing ${key}`);
  return row[key] as string;
};
const integer = (row: Record<string, unknown>, key: string) => {
  if (!Number.isInteger(row[key])) throw new Error(`Readiness response is missing ${key}`);
  return row[key] as number;
};
const list = <T>(value: unknown, parse: (item: unknown) => T): T[] => {
  if (!Array.isArray(value)) throw new Error("Readiness response must be a list");
  return value.map(parse);
};

const session = (value: unknown): ProctorSession => {
  const row = object(value, "Proctor session");
  return {
    exam_session_id: text(row, "exam_session_id"),
    assessment_title: text(row, "assessment_title"),
    cohort_name: text(row, "cohort_name"),
    starts_at: text(row, "starts_at"),
    ends_at: text(row, "ends_at"),
    state: text(row, "state"),
  };
};

const candidate = (value: unknown): ProctorCandidate => {
  const row = object(value, "Proctor candidate");
  const remaining = row.time_remaining_seconds;
  if (remaining !== null && !Number.isInteger(remaining))
    throw new Error("Readiness response has an invalid time_remaining_seconds");
  return {
    exam_session_id: text(row, "exam_session_id"),
    candidate_user_id: text(row, "candidate_user_id"),
    candidate_number: text(row, "candidate_number"),
    full_name: text(row, "full_name"),
    cohort_name: text(row, "cohort_name"),
    assessment_title: text(row, "assessment_title"),
    state: text(row, "state"),
    presence_state: text(row, "presence_state"),
    time_remaining_seconds: remaining as number | null,
    identity_status: text(row, "identity_status"),
    device_status: text(row, "device_status"),
    environment_status: text(row, "environment_status"),
    admission_version: integer(row, "admission_version"),
    admission_decided_at: row.admission_decided_at === null ? null : text(row, "admission_decided_at"),
  };
};

const readiness = (value: unknown): ProctorReadinessRecord => {
  const row = object(value, "Proctor readiness record");
  const kind = text(row, "check_type");
  if (!(["identity", "device", "environment"] as string[]).includes(kind))
    throw new Error("Readiness response has an invalid check_type");
  const schemaVersion = text(row, "schema_version");
  if (schemaVersion !== "d12-candidate-readiness.v1")
    throw new Error("Readiness response has an unsupported schema_version");
  return {
    id: text(row, "id"),
    exam_session_id: text(row, "exam_session_id"),
    candidate_user_id: text(row, "candidate_user_id"),
    check_type: kind as ProctorReadinessKind,
    status: text(row, "status"),
    method: text(row, "method"),
    observations: text(row, "observations"),
    recorded_by: text(row, "recorded_by"),
    recorded_at: text(row, "recorded_at"),
    version: integer(row, "version"),
    schema_version: schemaVersion,
    candidate_registration_id: text(row, "candidate_registration_id"),
    proctor_assignment_id: text(row, "proctor_assignment_id"),
    proctor_assignment_version: integer(row, "proctor_assignment_version"),
    authority_snapshot_hash: text(row, "authority_snapshot_hash"),
  };
};

const reentryCandidate = (value: unknown): ProctorReentryCandidate => {
  const row = object(value, "Candidate re-entry record");
  return {
    candidate_registration_id: text(row, "candidate_registration_id"),
    candidate_user_id: text(row, "candidate_user_id"),
    candidate_number: text(row, "candidate_number"),
    full_name: text(row, "full_name"),
    current_entry_sequence: integer(row, "current_entry_sequence"),
    max_reentries: integer(row, "max_reentries"),
    pending_approval_id: row.pending_approval_id === null ? null : text(row, "pending_approval_id"),
    pending_approval_sequence:
      row.pending_approval_sequence === null ? null : integer(row, "pending_approval_sequence"),
    can_approve: row.can_approve === true,
  };
};

const runtimeClock = (value: unknown): ProctorRuntimeClock => {
  const row = object(value, "Proctor runtime clock");
  const state = text(row, "clock_state");
  const schema = text(row, "clock_schema");
  if (
    !(["scheduled", "running", "paused", "ended"] as string[]).includes(state) ||
    schema !== "d12-exam-runtime-clock.v3" ||
    integer(row, "clock_version") !== 3 ||
    typeof row.admissions_locked !== "boolean"
  )
    throw new Error("Proctor runtime clock contract is unsupported");
  return {
    exam_session_id: text(row, "exam_session_id"),
    assessment_id: text(row, "assessment_id"),
    assessment_title: text(row, "assessment_title"),
    starts_at: text(row, "starts_at"),
    ends_at: text(row, "ends_at"),
    effective_ends_at: text(row, "effective_ends_at"),
    server_now: text(row, "server_now"),
    clock_state: state as ProctorRuntimeClock["clock_state"],
    duration_seconds: integer(row, "duration_seconds"),
    elapsed_seconds: integer(row, "elapsed_seconds"),
    remaining_seconds: integer(row, "remaining_seconds"),
    paused_seconds: integer(row, "paused_seconds"),
    pause_count: integer(row, "pause_count"),
    open_pause_id: row.open_pause_id === null ? null : text(row, "open_pause_id"),
    entered_candidate_count: integer(row, "entered_candidate_count"),
    admissions_locked: row.admissions_locked,
    clock_schema: schema as ProctorRuntimeClock["clock_schema"],
    clock_version: 3,
  };
};

const admissionLockReceipt = (value: unknown): SessionAdmissionLockReceipt => {
  const row = object(value, "Session admission lock receipt");
  if (
    text(row, "schema_version") !== "d12-session-admission-lock.v1" ||
    text(row, "control") !== "lock_new_admissions" ||
    row.existingAdmissionsChanged !== false ||
    row.attemptChanged !== false ||
    row.academicOutcome !== false
  )
    throw new Error("Session admission lock receipt is unsupported");
  return {
    id: text(row, "id"),
    exam_session_id: text(row, "exam_session_id"),
    locked_at: text(row, "locked_at"),
    schema_version: "d12-session-admission-lock.v1",
    control: "lock_new_admissions",
    existingAdmissionsChanged: false,
    attemptChanged: false,
    academicOutcome: false,
  };
};

const timerEventReceipt = (value: unknown): SessionTimerEventReceipt => {
  const row = object(value, "Session timer event receipt");
  const eventType = text(row, "event_type");
  const control = text(row, "control");
  if (
    text(row, "schema_version") !== "d12-session-timer-event.v1" ||
    !(["paused", "resumed"] as string[]).includes(eventType) ||
    !(["pause_timer", "resume_timer"] as string[]).includes(control) ||
    row.extensionApplied !== false ||
    row.attemptChanged !== false ||
    row.academicOutcome !== false
  )
    throw new Error("Session timer event receipt is unsupported");
  return {
    id: text(row, "id"),
    exam_session_id: text(row, "exam_session_id"),
    sequence: integer(row, "sequence"),
    event_type: eventType as SessionTimerEventReceipt["event_type"],
    pause_id: text(row, "pause_id"),
    occurred_at: text(row, "occurred_at"),
    schema_version: "d12-session-timer-event.v1",
    control: control as SessionTimerEventReceipt["control"],
    extensionApplied: false,
    attemptChanged: false,
    academicOutcome: false,
  };
};

const send = createVersionedCommandSender("d12-candidate-readiness");
const sendAdmission = createVersionedCommandSender("d12-candidate-admission");
const sendReentryApproval = createVersionedCommandSender("d12-candidate-reentry-approval");
const sendRoomControl = createVersionedCommandSender("d12-session-admission-lock");
const sendTimerControl = createVersionedCommandSender("d12-session-timer-event");

export async function listProctorSessions(signal?: AbortSignal) {
  return list(
    await academyVersionRequest<unknown>(
      "v1/proctor/sessions?limit=200",
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
    session,
  );
}

export async function loadProctorRuntimeClock(examSessionId: string, signal?: AbortSignal) {
  return runtimeClock(
    await academyVersionRequest<unknown>(
      `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/runtime-clock`,
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
  );
}

export async function lockProctorSessionAdmissions(examSessionId: string, reason: string) {
  return admissionLockReceipt(
    await sendRoomControl<unknown>(`v1/proctor/sessions/${encodeURIComponent(examSessionId)}/controls`, {
      control: "lock_new_admissions",
      reason,
    }),
  );
}

export async function applyProctorSessionTimerControl(
  examSessionId: string,
  control: "pause_timer" | "resume_timer",
  reason: string,
  pauseId?: string,
) {
  return timerEventReceipt(
    await sendTimerControl<unknown>(`v1/proctor/sessions/${encodeURIComponent(examSessionId)}/controls`, {
      control,
      reason,
      ...(pauseId ? { pauseId } : {}),
    }),
  );
}

export async function loadProctorReadiness(examSessionId: string, signal?: AbortSignal) {
  const encoded = encodeURIComponent(examSessionId);
  const [candidateRows, readinessRows] = await Promise.all([
    academyVersionRequest<unknown>(
      `v1/proctor/sessions/${encoded}/candidates?limit=250`,
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
    academyVersionRequest<unknown>(
      `v1/proctor/sessions/${encoded}/readiness`,
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
  ]);
  return {
    candidates: list(candidateRows, candidate),
    records: list(readinessRows, readiness),
  };
}

export async function listProctorCandidates(examSessionId: string, signal?: AbortSignal) {
  return list(
    await academyVersionRequest<unknown>(
      `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/candidates?limit=250`,
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
    candidate,
  );
}

export function recordProctorReadiness(
  examSessionId: string,
  kind: ProctorReadinessKind,
  input: RecordReadinessInput,
) {
  return send<unknown>(`v1/proctor/sessions/${encodeURIComponent(examSessionId)}/readiness/${kind}`, input);
}

export function decideCandidateAdmission(
  examSessionId: string,
  input: {
    candidateUserId: string;
    decision: "admitted" | "review_required" | "denied";
    reason: string;
    expectedVersion: number;
  },
) {
  return sendAdmission<unknown>(`v1/proctor/sessions/${encodeURIComponent(examSessionId)}/admissions`, input);
}

export async function listCandidateReentryApprovals(examSessionId: string, signal?: AbortSignal) {
  return list(
    await academyVersionRequest<unknown>(
      `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/reentry-candidates`,
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
    reentryCandidate,
  );
}

export function approveCandidateReentry(
  examSessionId: string,
  input: { candidateUserId: string; expectedEntrySequence: number; reason: string },
) {
  return sendReentryApproval<unknown>(
    `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/reentry-approvals`,
    input,
  );
}
